/**
 * `/act-as` — entrada a la app con el token de impersonación de la consola de
 * plataforma, sin pasar por el login (trd-client#92 · trd-platform#11).
 *
 * El token ya existe y el borde ya lo acepta: identity emite uno act-as de 15
 * minutos con el claim `act` (RFC 8693) y el BFF lo trata como delegación. Lo
 * único que faltaba era la puerta de entrada del cliente.
 *
 * ⚠️ EL TOKEN VIAJA EN EL FRAGMENTO, NUNCA EN LA QUERY:
 *
 *     ✅  /act-as#token=<jwt>
 *     ❌  /act-as?token=<jwt>
 *
 * El fragmento no se manda al servidor: no queda en los logs del ingress, ni en
 * el APM, ni en el `Referer` que el browser manda a terceros. La query queda en
 * los tres. Es una credencial de 15 minutos CON LA IDENTIDAD DE OTRA PERSONA.
 *
 * Y se limpia de la URL apenas se lee (`history.replaceState`), incluso cuando
 * el handoff falla, para que no quede en la barra ni en el historial.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { meUserWithOrg } from "@/context/auth/authUtils";
import {
  clearSession,
  saveIdentitySession,
  sessionSnapshot,
} from "@/context/auth/sessionRenewal";
import { IpcRest } from "@/hooks/ipc";
import { useToast } from "@/hooks/use-toast";
import loadStore from "@/utils/generateStore";

/** Lee `token` del fragmento y lo borra de la URL en el mismo paso. */
function takeTokenFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  const token = new URLSearchParams(hash).get("token");
  // Se limpia SIEMPRE, haya token o no: si el link se pega mal, tampoco queremos
  // el resto del fragmento dando vueltas en el historial.
  window.history.replaceState(null, "", window.location.pathname);
  return token && token.trim() ? token : null;
}

function ActAsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [message, setMessage] = useState("Entrando…");
  // React monta dos veces en StrictMode; el handoff consume el fragmento, así que
  // la segunda pasada no encontraría token y mandaría a /login por las malas.
  const alreadyRan = useRef(false);

  useEffect(() => {
    if (alreadyRan.current) return;
    alreadyRan.current = true;

    const bail = (description: string) => {
      setMessage(description);
      toast({
        title: "No se pudo acceder",
        description,
        variant: "destructive",
        duration: 6000,
      });
      navigate("/login", { replace: true });
    };

    (async () => {
      const token = takeTokenFromHash();
      if (!token) {
        bail(
          "El enlace no trae un token de acceso. Generá uno nuevo desde la consola."
        );
        return;
      }

      const store = await loadStore();
      await clearSession(store);
      const before = await sessionSnapshot(store);
      try {
        const me = await IpcRest.get<any>({
          url: "/api/v1/auth/me",
          headers: { Authorization: `Bearer ${token}` },
        });
        const meUser = meUserWithOrg(me);
        if (!meUser) throw new Error("El /me no devolvió usuario");

        await saveIdentitySession(store, token, undefined, meUser, before);
        navigate("/", { replace: true });
      } catch {
        // Nunca dejar media sesión colgada: si el handoff falla, se limpia todo
        // antes de mandar al login.
        try {
          await clearSession(store, before);
        } catch {
          return;
        }
        bail(
          "El enlace de acceso venció o no es válido. Generá uno nuevo desde la consola."
        );
      }
    })();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-2 bg-background-100">
      <p className="text-lg">{message}</p>
      <p className="text-sm text-muted-foreground">
        Accediendo con una sesión de soporte.
      </p>
    </div>
  );
}

export default ActAsPage;
